import { toast } from 'react-toastify';
import React, { useState, useEffect, useContext } from 'react';
import axios, { clearAxiosCache } from '../../axios-config';
import { startNotificationConnection, OrderNotification } from '../../services/notificationHubService';   // ★ NEW
import {
  Plus,
  Search,
  XCircle,
  Download,
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import { AcornOrderStatuses } from '../../types/enums';
import { badgeService } from '../../services/badgeService';
import mediaService from '../../services/mediaService';
import MediaChooser from '../common/MediaChooser';

// Define types based on API response
type Product = {
  productId: number;
  storeId: number; // NEW from sample
  productName: string;
  productDescription: string;
  imageUrl: string;
  pointCost: number;
  salePointCost: number; // NEW from sample
  stockQuantity: number;
  productUnlimited: boolean;
  maxPerStudent: number;
  featuredStart?: string;
  featuredEnd?: string;
  pickupLocation?: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string; // NEW from sample
  requiredBadgeId?: string;
  requiredBadgeLevel?: number;
  requiredBadgeLevelId?: number | null;
};

type Order = {
  acornOrderId: number;
  userId: string;
  storeId: number;
  acornOrderDate: string;
  acornOrderStatusId: number;
  pickupLocation: string;
  pickupCode?: string;
  orderNotes?: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  studentName?: string;
  studentAvatar?: string;
  items?: OrderItem[];
  total?: number;
};

type OrderItem = {
  acornOrderItemId: number;
  acornOrderId:    number;
  productId:       number;
  pointCost:       number;
  quantity:        number;
  products:        Product;
};

type Store = {
  storeId: number;
  organizationId: string;
  storeName: string;
  storeSlug: string;
  storeTypeId: number;
  openingTime: string;
  closingTime: string;
  products: Product[];
  organization: any;
  storeOperatingDays: any[];
  orders?: Order[];        // ★ NEW – used elsewhere already
};

type OrderStatusMapping = {
  [key: number]: 'pending' | 'processing' | 'ready' | 'completed' | 'cancelled';
};

// Helper function to get item name with robust fallback logic
const getItemName = (item: any, allProducts?: Product[]): string => {
  // Try multiple possible data structures from API response
  if (item.products?.productName) {
    return item.products.productName;
  } else if (item.product?.productName) {
    return item.product.productName;
  } else if (item.productName) {
    return item.productName;
  } else if (item.name) {
    return item.name;
  } else if (item.product?.name) {
    return item.product.name;
  } else if (item.products?.name) {
    return item.products.name;
  }
  
  // Try to lookup product by ID if we have access to all products
  if (item.productId && allProducts) {
    const foundProduct = allProducts.find(p => p.productId === item.productId);
    if (foundProduct?.productName) {
      return foundProduct.productName;
    }
  }
  
  return 'Unknown item';
};

export default function ShopManagement({ activeTab }: { activeTab: 'products' | 'auctions' | 'transactions' | 'orders' }) {
  const [stores, setStores] = useState<Store[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  // New state for editing product
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showEditProduct, setShowEditProduct] = useState(false);
  // NEW: Stock type states for forms
  const [addStockType, setAddStockType] = useState<'limited' | 'unlimited'>('limited');
  const [editStockType, setEditStockType] = useState<'limited' | 'unlimited'>('limited');
  const [addFeatured, setAddFeatured] = useState<boolean>(false);
  const [editFeatured, setEditFeatured] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [updatedQuantities, setUpdatedQuantities] = useState({});
  const [badgeOptions, setBadgeOptions] = useState<{ badgeLevelId: number; badgeId: string; levelNumber: number; label: string; }[]>([]);
  const [addBadgeLevelId, setAddBadgeLevelId] = useState<string>('');
  const [addImageUrl, setAddImageUrl] = useState<string>('');
  const [editImageUrl, setEditImageUrl] = useState<string>('');
  
  const { userProfile } = useContext(UserContext);

  // Map order status IDs to readable status
  const orderStatusMap: OrderStatusMapping = {
    1: 'pending',
    2: 'processing',
    3: 'ready',
    4: 'completed',
    5: 'cancelled'
  };

  // ★ ADD: helper to sort newest → oldest
  const sortOrdersDesc = (arr: Order[]) =>
    [...arr].sort(
      (a, b) =>
        new Date(b.acornOrderDate).getTime() -
        new Date(a.acornOrderDate).getTime()
    );

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get('/api/Stores');
        const fetchedStores: Store[] = response.data.stores;
        setStores(fetchedStores);

        const allExtractedOrders: Order[] = [];
        if (fetchedStores && fetchedStores.length > 0) {
          fetchedStores.forEach(store => {
            if (store.orders && store.orders.length > 0) {
              store.orders.forEach(order => {
                allExtractedOrders.push({
                  ...order
                });
              });
            }
          });
        }
        setOrders(sortOrdersDesc(allExtractedOrders));   // ⬅ sort initially
      } catch (error) {
        console.error('Error fetching stores:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStores();
  }, []); // useEffect still runs once on mount

  useEffect(() => {
    (async () => {
      try {
        const badges = await badgeService.getBadges();
        const options = badges.flatMap(b =>
          b.badgeLevels.map(l => ({
            badgeLevelId: l.badgeLevelId,
            badgeId: b.badgeId,
            levelNumber: l.levelNumber,
            label: `${b.badgeName} - ${l.levelName}`
          }))
        );
        setBadgeOptions(options);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  // Get the active store (using selectedLocation instead of always the first)
  const activeStore = selectedLocation === 'all'
    ? (stores.length > 0 ? stores[0] : null)
    : stores.find(store => store.storeName === selectedLocation) || null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'completed':
        return 'bg-green-50 text-green-600';
      case 'draft':
        return 'bg-gray-50 text-gray-600';
      case 'out-of-stock':
        return 'bg-red-50 text-red-600';
      case 'pending':
        return 'bg-yellow-50 text-yellow-600';
      case 'processing':
        return 'bg-orange-50 text-orange-600';
      case 'ready':
        return 'bg-blue-50 text-blue-600';
      case 'cancelled':
        return 'bg-red-50 text-red-600';
      case 'ended':
        return 'bg-gray-50 text-gray-600';
      case 'scheduled':
        return 'bg-blue-50 text-blue-600';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  // Determine if a product is featured (current date is between featuredStart and featuredEnd)
  const isProductFeatured = (product: Product): boolean => {
    if (!product.featuredStart || !product.featuredEnd) return false;
    
    const now = new Date();
    const start = new Date(product.featuredStart);
    const end = new Date(product.featuredEnd);
    
    return now >= start && now <= end;
  };

  // Determine product status based on stock
  const getProductStatus = (product: Product): 'active' | 'draft' | 'out-of-stock' => {
    if (product.productUnlimited) return 'active';
    return product.stockQuantity > 0 ? 'active' : 'out-of-stock';
  };

  // Filter products based on search term, category, and location
  const allProducts = stores.flatMap(s => s.products);
  const productsToFilter = selectedLocation === 'all'
    ? allProducts
    : (activeStore?.products || []);
  const filteredProducts = productsToFilter.filter(product => {
      const matchesSearch =
        searchTerm === '' ||
        product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.productDescription || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === '' ||
        (product.productDescription || '').includes(selectedCategory);

      return matchesSearch && matchesCategory;
    });

  // Filter orders based on search term, status, and location
  const sortedOrders = sortOrdersDesc(orders);           // ensure always sorted
  const filteredOrders = sortedOrders.filter(order => {
      const student = userProfile?.students?.find(s => s.userId === order.userId);
      const studentName = student?.name?.toLowerCase() ?? '';
      const matchesSearch =
        searchTerm === '' ||
        studentName.includes(searchTerm.toLowerCase()) ||
        String(order.acornOrderId).includes(searchTerm);

      const matchesStatus =
        selectedStatus === 'all' ||
        orderStatusMap[order.acornOrderStatusId] === selectedStatus;

     // match by store selection
     const matchesLocation =
       selectedLocation === 'all' ||
       order.storeId ===
         (stores.find(s => s.storeName === selectedLocation)?.storeId);

      return matchesSearch && matchesStatus && matchesLocation;
    });

  // NEW: Handler to open edit modal with product data
  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    // Set radio button based on productUnlimited flag
    setEditStockType(product.productUnlimited ? 'unlimited' : 'limited');
    setEditFeatured(!!(product.featuredStart && product.featuredEnd));
    setEditImageUrl(product.imageUrl || '');
    setShowEditProduct(true);
  };

  const handleAddBadgeLevelSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAddBadgeLevelId(e.target.value);
  };

  const handleEditBadgeLevelSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!editingProduct) return;
    const value = e.target.value;
    const levelId = value ? Number(value) : null;
    let badgeId = '';
    let levelNumber = 0;
    if (levelId !== null) {
      const opt = badgeOptions.find(o => o.badgeLevelId === levelId);
      if (opt) {
        badgeId = opt.badgeId;
        levelNumber = opt.levelNumber;
      }
    }
    setEditingProduct({
      ...editingProduct,
      requiredBadgeId: badgeId,
      requiredBadgeLevel: levelNumber,
      requiredBadgeLevelId: levelId,
    });
  };

  // Add helper to convert UTC to local datetime-local string
  const convertUTCtoLocal = (utcString: string) => {
    const date = new Date(utcString);
    const tzOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - tzOffset);
    return localDate.toISOString().slice(0, 16);
  };

  // ★ NEW: Handler to add a product
  const handleAddProduct = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!activeStore) return toast.error('Select a store first.');

    const formData = new FormData(e.currentTarget);

    const localFeaturedStart = formData.get('featuredStart')?.toString().trim();
    const localFeaturedEnd   = formData.get('featuredEnd')?.toString().trim();

    const newProduct = {
      storeId: activeStore.storeId,
      productName:       (formData.get('productName')       || '').toString().trim(),
      productDescription:(formData.get('productDescription')|| '').toString().trim(),
      pointCost:         Number(formData.get('pointCost'))        || 0,
      salePointCost:     Number(formData.get('salePointCost'))    || 0,
      stockQuantity:     addStockType === 'limited'   ? Number(formData.get('stockQuantity'))  || 0 : 0,
      maxPerStudent:     addStockType === 'limited'   ? Number(formData.get('maxPerStudent'))  || 0 : 0,
      imageUrl:          (formData.get('imageUrl') || '').toString().trim(),
      pickupLocation:    (formData.get('pickupLocation') || '').toString().trim(),
      productUnlimited:  addStockType === 'unlimited',
      featuredStart: addFeatured && localFeaturedStart ? new Date(localFeaturedStart).toISOString() : null,
      featuredEnd:   addFeatured && localFeaturedEnd   ? new Date(localFeaturedEnd).toISOString()   : null,
      requiredBadgeLevelId: addBadgeLevelId ? Number(addBadgeLevelId) : null
    };

    try {
      const { data } = await axios.post('/api/Products', newProduct);
      setStores(prev =>
        prev.map(s =>
          s.storeId === activeStore.storeId
            ? { ...s, products: [...s.products, data] }
            : s
        )
      );
      setShowAddProduct(false);
      clearAxiosCache();
      toast.success('Product added successfully');
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error('Failed to add product');
    }
  };

  // NEW: Handler to update product using API call
  const handleUpdateProduct = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingProduct) return;
    const formData = new FormData(e.currentTarget);

    const localFeaturedStart = formData.get('featuredStart')?.toString().trim();
    const localFeaturedEnd = formData.get('featuredEnd')?.toString().trim();

    const updatedProduct = {
      ...editingProduct,
      productName: formData.get('productName')?.toString().trim() || editingProduct.productName,
      productDescription: formData.get('productDescription')?.toString().trim() || editingProduct.productDescription,
      pointCost: Number(formData.get('pointCost')) || editingProduct.pointCost,
      salePointCost: Number(formData.get('salePointCost')) || editingProduct.salePointCost,
      stockQuantity: editStockType === 'limited'
        ? Number(formData.get('stockQuantity')) || editingProduct.stockQuantity
        : 0,
      maxPerStudent: editStockType === 'limited'
        ? Number(formData.get('maxPerStudent')) || editingProduct.maxPerStudent
        : 0,
      imageUrl: formData.get('imageUrl')?.toString().trim() || editingProduct.imageUrl,
      pickupLocation: formData.get('pickupLocation')?.toString().trim() || editingProduct.pickupLocation,
      productUnlimited: editStockType === 'unlimited',
      featuredStart: localFeaturedStart ? new Date(localFeaturedStart).toISOString() : null,
      featuredEnd: localFeaturedEnd ? new Date(localFeaturedEnd).toISOString() : null,
      requiredBadgeLevelId: editingProduct.requiredBadgeLevelId
    };
    try {
      const response = await axios.put(`/api/Products/${editingProduct.productId}`, updatedProduct);
      // Update products in active store (assuming activeStore is stores[0])
      setStores(prevStores => prevStores.map(store => {
        if(store.storeId === activeStore?.storeId) {
          return {
            ...store,
            products: store.products.map(p => p.productId === editingProduct.productId ? response.data : p)
          };
        }
        return store;
      }));
      setShowEditProduct(false);
      setEditingProduct(null);
      clearAxiosCache();
    } catch(error) {
      console.error('Error updating product:', error);
    }
  };

  // NEW: Handler to delete product using API call
  const handleDeleteProduct = async (product: Product) => {
    if(!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`/api/Products/${product.productId}`);
      // Remove the product from any store that contains it (compare as strings to avoid type mismatches)
      setStores(prevStores => prevStores.map(store => ({
        ...store,
        products: store.products.filter(p => String(p.productId) !== String(product.productId))
      })));
      clearAxiosCache();
      toast.success('Product deleted successfully');
    } catch(error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  // NEW: Order action handlers
  const handleMarkReady = async (order: Order) => {
    // Calls endpoint to mark the order as ready
    try {
      await axios.put(`/api/Orders/${order.acornOrderId}/markready`);
      setOrders(prev =>
        prev.map(o =>
          o.acornOrderId === order.acornOrderId ? { ...o, acornOrderStatusId: 3 } : o
        )
      );
    } catch (error) {
      console.error('Error marking order ready', error);
    }
  };

  const handleProcessOrder = async (order: Order) => {
    try {
      await axios.put(`/api/Orders/${order.acornOrderId}`, { acornOrderStatusId: AcornOrderStatuses.Pending, acornOrderId: order.acornOrderId });
      setOrders(prev => prev.map(o => o.acornOrderId === order.acornOrderId ? { ...o, acornOrderStatusId: 2 } : o));
    } catch (error) {
      console.error('Error processing order:', error);
    }
  };

  const handleCompleteDelivery = async (order: Order) => {
    try {
      await axios.put(`/api/Orders/${order.acornOrderId}/completeDelivery`);
      setOrders(prev => prev.map(o => o.acornOrderId === order.acornOrderId ? { ...o, acornOrderStatusId: 4 } : o));
    } catch (error) {
      console.error('Error completing delivery:', error);
    }
  };

  const handleReturnToProcessing = async (order: Order) => {
    try {
      await axios.put(`/api/Orders/${order.acornOrderId}/returnToProcessing`);
      setOrders(prev => prev.map(o => o.acornOrderId === order.acornOrderId ? { ...o, acornOrderStatusId: 2 } : o));
    } catch (error) {
      console.error('Error returning to processing:', error);
    }
  };

  const handleCancelOrder = async (order: Order) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      await axios.put(`/api/Orders/${order.acornOrderId}/cancel`);
      setOrders(prev =>
        prev.map(o =>
          o.acornOrderId === order.acornOrderId ? { ...o, acornOrderStatusId: 5 } : o
        )
      );
    } catch (error) {
      console.error('Error cancelling order:', error);
    }
  };

  const handleRefundOrder = async (order: Order) => {
    if (!window.confirm('Are you sure you want to refund this order?')) return;
    try {
      await axios.put(`/api/Orders/${order.acornOrderId}/refund`);
      setOrders(prev =>
        prev.map(o =>
          o.acornOrderId === order.acornOrderId ? { ...o, acornOrderStatusId: 5 } : o
        )
      );
    } catch (error) {
      console.error('Error refunding order:', error);
    }
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  const handleCloseOrderDetail = () => {
    setSelectedOrder(null);
    setShowOrderDetail(false);
  };

  function handleChangeItemQuantity(itemId: number, value: string) {
    setUpdatedQuantities(prev => ({ ...prev, [itemId]: parseInt(value) || 0 }));
  }

  const handleUpdateOrder = async () => {
    // Placeholder for saving new quantities, etc.
    // Here you can send updatedQuantities to your API
    toast.success("Updated quantities:", updatedQuantities);
  };

  // ★ NEW – SignalR order notifications
  useEffect(() => {
    let conn: any;
    const handleOrderEvent = async (payload: OrderNotification) => {
      const { type, orderId } = payload;

      if (type === 'new') {
        try {
          const { data } = await axios.get(`/api/Orders/${orderId}`);
          setOrders(prev => {
            // avoid dupes
            if (prev.some(o => o.acornOrderId === orderId)) return prev;
            return sortOrdersDesc([data, ...prev]);           // prepend & sort
          });
          // toast.info(`New order #${orderId} received`);
        } catch (e) {
          console.error('Error fetching new order:', e);
        }
      } else if (type === 'cancel') {
        setOrders(prev => {
          const exists = prev.some(o => o.acornOrderId === orderId);
          if (exists) {
            return prev.map(o =>
              o.acornOrderId === orderId ? { ...o, acornOrderStatusId: 5 } : o
            );
          }
          return prev;   // keep state as-is, we’ll fetch below
        });

        // fetch only if order not already present
        if (!orders.some(o => o.acornOrderId === orderId)) {
          try {
            const { data } = await axios.get(`/api/Orders/${orderId}`);
            setOrders(prev =>
              sortOrdersDesc([{ ...data, acornOrderStatusId: 5 }, ...prev])
            );
          } catch (e) {
            console.error('Error fetching cancelled order:', e);
          }
        }

        // toast.info(`Order #${orderId} cancelled`);
      } else if (type === 'ready') {
        setOrders(prev =>
          prev.map(o =>
            o.acornOrderId === orderId
              ? { ...o, acornOrderStatusId: AcornOrderStatuses.Pending }
              : o
          )
        );
      }
    };

    startNotificationConnection(
      undefined,
      'nonStudent',
      (payload: OrderNotification): void => {
        handleOrderEvent(payload);
      }
    )
      .then(c => (conn = c));

    return () => {
      conn?.off?.("OrderPostAcornOrder");
      conn?.off?.("OrderCancel");
    };
  }, []); // once on mount

  if (isLoading) {
    return <div className="text-center py-10">Loading store data...</div>;
  }

  return (
    <>
      {activeTab === 'products' && (
        <div className="space-y-6">
          {/* Actions Bar */}
          <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-4 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg w-full sm:w-64 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                >
                  <option value="">All Categories</option>
                  <option value="Supplies">Supplies</option>
                  <option value="Access">Access Passes</option>
                  <option value="Kit">Kits</option>
                </select>

                {/* NEW: Location dropdown */}
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                >
                  <option value="all">All Locations</option>
                  {stores.map(store => (
                    <option key={store.storeId} value={store.storeName}>
                      {store.storeName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <button className="px-4 py-2 bg-white/50 text-gray-700 rounded-lg hover:bg-white transition-colors flex items-center gap-2 w-full sm:w-auto justify-center">
                  <Download className="w-4 h-4" />
                  Export
                </button>
                <button
                  onClick={() => setShowAddProduct(true)}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                >
                  <Plus className="w-4 h-4" />
                  Add Product
                </button>
              </div>
            </div>
          </div>

          {/* Hide Products Grid while adding or editing */}
          {(!showAddProduct && !showEditProduct) && (
            activeStore ? (
              /* Products Grid */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product, index) => (
                    <div
                      key={product.productId || `product-${index}`}
                      className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 overflow-hidden"
                    >
                      <div className="relative">
                        <img
                          src={mediaService.getDirectUrl(userProfile?.orgId || '', product.imageUrl)}
                          alt={product.productName}
                          className="w-full h-48 object-cover"
                        />
                        {isProductFeatured(product) && (
                          <div className="absolute top-2 left-2 bg-yellow-400 text-xs font-bold px-2 py-1 rounded-full text-white">
                            Featured
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">{product.productName}</h3>
                            <p className="text-sm text-gray-500">
                              {(product.productDescription || '').includes('Supplies') ? 'Supplies' : (product.productDescription || '').includes('Access') ? 'Access Pass' : 'Kit'}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(getProductStatus(product))}`}>
                            {getProductStatus(product) === 'out-of-stock' ? 'Out of Stock' : 
                             getProductStatus(product).charAt(0).toUpperCase() + getProductStatus(product).slice(1)}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">{product.productDescription}</p>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Points</span>
                            <span className="font-medium text-gray-800">{product.pointCost}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Sale Points</span>
                            <span className="font-medium text-gray-800">{product.salePointCost}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">In Stock</span>
                            <span className="font-medium text-gray-800">
                              {product.productUnlimited ? 'Unlimited' : product.stockQuantity}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Max Per Student</span>
                            <span className="font-medium text-gray-800">{product.maxPerStudent}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Pickup Location</span>
                            <span className="font-medium text-gray-800">{product.pickupLocation || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* UPDATED: Call edit handler */}
                            <button onClick={() => handleEditProduct(product)} className="flex-1 bg-indigo-50 text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium">
                              Edit
                            </button>
                            {/* UPDATED: Call delete handler */}
                            <button onClick={() => handleDeleteProduct(product)} className="flex-1 bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium">
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-3 text-center py-10 text-gray-500">
                    No products found matching your search criteria.
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">
                No store found. Please create a store first.
              </div>
            )
          )}
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="space-y-6">
          {/* Actions Bar */}
          <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-4 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg w-full sm:w-64 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                </div>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="ready">Ready for Pickup</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                >
                  <option value="all">All Locations</option>
                  {stores.map(store => (
                    <option key={store.storeId} value={store.storeName}>
                      {store.storeName}
                    </option>
                  ))}
                </select>
              </div>
              <button className="px-4 py-2 bg-white/50 text-gray-700 rounded-lg hover:bg-white transition-colors flex items-center gap-2 w-full sm:w-auto justify-center">
                <Download className="w-4 h-4" />
                Export Orders
              </button>
            </div>
          </div>

          {/* Orders List */}
          <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">Order Management</h2>
            </div>
            
            {!showOrderDetail && (
              <div className="divide-y divide-gray-100">
                {filteredOrders.map(order => {
                  const student = userProfile?.students?.find(s => s.userId === order.userId);
                  const displayName = student?.name ?? 'N/A';
                  const displayAvatar = student?.avatar ?? 'https://via.placeholder.com/100';
                  const studentId = student?.altId ?? 'N/A';
                  return (
                    <div key={order.acornOrderId} className="p-6 hover:bg-indigo-50/50 transition-colors">
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <img
                            src={displayAvatar}
                            alt={displayName}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="font-medium text-gray-800">{displayName}</h3>
                              <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(orderStatusMap[order.acornOrderStatusId] || 'pending')}`}>
                                {orderStatusMap[order.acornOrderStatusId]?.charAt(0).toUpperCase() + 
                                 orderStatusMap[order.acornOrderStatusId]?.slice(1) || 'Pending'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">Order #{order.acornOrderId} • Student ID: {studentId}</p>
                            <p className="text-sm text-gray-500">
                              Ordered: {new Date(order.acornOrderDate).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="text-right">
                            <p className="font-medium text-gray-800">{order.total || 0} pts</p>
                            <div className="text-sm text-gray-500">
                              {order.items && order.items.length > 0 ? (
                                <div className="text-right space-y-1">
                                  {order.items.map((item, index) => (
                                    <div key={index}>
                                      {item.quantity}x {getItemName(item, allProducts)}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p>No items found</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {orderStatusMap[order.acornOrderStatusId] === 'pending' && (
                              <button onClick={() => handleProcessOrder(order)} className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors">
                                Process Order
                              </button>
                            )}
                            {orderStatusMap[order.acornOrderStatusId] === 'processing' && (
                              <button
                                onClick={() => handleMarkReady(order)}
                                className="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
                              >
                                Mark Ready
                              </button>
                            )}
                            {orderStatusMap[order.acornOrderStatusId] === 'ready' && (
                              <button onClick={() => handleCompleteDelivery(order)} className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
                                Complete Pickup
                              </button>
                            )}
                            {['pending', 'processing'].includes(orderStatusMap[order.acornOrderStatusId] || '') && (
                              <button
                                onClick={() => handleCancelOrder(order)}
                                className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
                              >
                                Cancel
                              </button>
                            )}
                            {['pending', 'processing'].includes(orderStatusMap[order.acornOrderStatusId] || '') && (
                              <button
                                onClick={() => handleRefundOrder(order)}
                                className="px-3 py-1.5 bg-orange-50 text-red-600 rounded-lg text-sm font-medium hover:bg-orange-100 transition-colors"
                              >
                                Refund
                              </button>
                            )}
                            <button
                              onClick={() => handleViewOrder(order)}
                              className="px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                            >
                              Manage
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Order Details */}
                      <div className="mt-4 pl-16">
                        <div className="bg-gray-50/50 rounded-lg p-4">
                          {/* Order Items */}
                          {order.items && order.items.length > 0 && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">Items Ordered:</p>
                              <div className="space-y-2">
                                {order.items.map((item, index) => (
                                  <div key={index} className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600">
                                      {getItemName(item, allProducts)}
                                    </span>
                                    <span className="text-gray-800 font-medium">
                                      Qty: {item.quantity} @ {item.pointCost} pts each
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="pt-4 border-t border-gray-200">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm font-medium text-gray-700 mb-1">Pickup Location</p>
                                <p className="text-sm text-gray-600">
                                  {order.pickupLocation || 'Not specified'}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700 mb-1">Pickup Code</p>
                                <p className="text-sm font-mono text-gray-600">{order.pickupCode || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {showOrderDetail && selectedOrder && (
              <div className="bg-white p-6 mt-4 rounded-xl">
                <h2 className="text-lg font-semibold">
                  Order #{selectedOrder.acornOrderId}
                </h2>
                <div className="mt-4 space-y-3">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <p>{getItemName(item, allProducts)}</p>
                      <input
                        type="number"
                        defaultValue={item.quantity}
                        className="border border-gray-300 w-20 rounded px-2"
                        onChange={(e) => handleChangeItemQuantity(item.productId, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleUpdateOrder}
                    className="bg-indigo-100 text-indigo-600 px-3 py-1.5 rounded hover:bg-indigo-200 text-sm font-medium"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => handleMarkReady(selectedOrder)}
                    className="bg-green-50 text-green-600 px-3 py-1.5 rounded hover:bg-green-100 text-sm font-medium"
                  >
                    Mark Ready
                  </button>
                  <button
                    onClick={() => handleCancelOrder(selectedOrder)}
                    className="bg-red-50 text-red-600 px-3 py-1.5 rounded hover:bg-red-100 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleRefundOrder(selectedOrder)}
                    className="bg-orange-50 text-red-600 px-3 py-1.5 rounded hover:bg-orange-100 text-sm font-medium"
                  >
                    Refund
                  </button>
                  <button onClick={() => handleReturnToProcessing(selectedOrder)}
                    className="bg-yellow-50 text-yellow-600 px-3 py-1.5 rounded hover:bg-yellow-100 text-sm font-medium"
                  >
                    Return to Processing
                  </button>
                  <button
                    onClick={handleCloseOrderDetail}
                    className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded hover:bg-gray-200 text-sm font-medium"
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'auctions' && (
        <div className="text-center py-10 text-gray-500">
          Auctions feature is not implemented yet.
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="text-center py-10 text-gray-500">
          Transactions feature is not implemented yet.
        </div>
      )}

      {/* Inline Add Product Component */}
      {showAddProduct && (
        <div className="my-4 p-8 bg-white rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Add New Product</h3>
            <button
              onClick={() => setShowAddProduct(false)}
              className="p-2 hover:bg-gray-100/80 rounded-full transition-colors"
            >
              <XCircle className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <form className="space-y-6" onSubmit={handleAddProduct}>
            {/* Form fields for adding a product */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Name
              </label>
              <input
                name="productName"
                type="text"
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                placeholder="Enter product name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="productDescription"
                rows={3}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                placeholder="Enter product description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Points
                </label>
                <input
                  name="pointCost"
                  type="number"
                  className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  placeholder="Enter points"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sale Points
                </label>
                <input
                  name="salePointCost"
                  type="number"
                  className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  placeholder="Enter sale points"
                />
              </div>
            </div>

            <div>
              <p className="block text-sm font-medium text-gray-700 mb-2">
                Stock Type
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="limited-stock"
                    name="stockType"
                    value="limited"
                    checked={addStockType === 'limited'}
                    onChange={() => setAddStockType('limited')}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="limited-stock" className="text-sm text-gray-700">
                    Limited Stock
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="unlimited-stock"
                    name="stockType"
                    value="unlimited"
                    checked={addStockType === 'unlimited'}
                    onChange={() => setAddStockType('unlimited')}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="unlimited-stock" className="text-sm text-gray-700">
                    Unlimited Stock
                  </label>
                </div>
              </div>
            </div>

            {/* Only show stock quantity and max per student fields if stock type is limited */}
            {addStockType === 'limited' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock
                  </label>
                  <input
                    name="stockQuantity"
                    type="number"
                    className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                    placeholder="Enter stock"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Per Student
                  </label>
                  <input
                    name="maxPerStudent"
                    type="number"
                    className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                    placeholder="Enter max per student"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
              <MediaChooser
                label="Product Image"
                value={addImageUrl}
                onChange={(v) => setAddImageUrl(v)}
                allowManualUrlEntry={true}
                buttonText="Choose Image"
                clearable={true}
                imagesOnly={true}
              />
              <input type="hidden" name="imageUrl" value={addImageUrl} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pickup Location
              </label>
              <input
                type="text"
                name="pickupLocation"
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                placeholder="e.g., Room 101, Main Office"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Required Badge Level
              </label>
              <select
                name="requiredBadgeLevelId"
                value={addBadgeLevelId}
                onChange={handleAddBadgeLevelSelect}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              >
                <option value="">None</option>
                {badgeOptions.map(opt => (
                  <option key={opt.badgeLevelId} value={opt.badgeLevelId}>{opt.label}</option>
                ))}
              </select>
            </div>
            {/* NEW: Featured Status for Add Modal */}
            <div className="flex flex-col">
              <p className="block text-sm font-medium text-gray-700 mb-2">Featured Status</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="add-not-featured"
                    name="featuredStatus"
                    value="notFeatured"
                    checked={!addFeatured}
                    onChange={() => setAddFeatured(false)}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="add-not-featured" className="text-sm text-gray-700">Not Featured</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="add-featured"
                    name="featuredStatus"
                    value="featured"
                    checked={addFeatured}
                    onChange={() => setAddFeatured(true)}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="add-featured" className="text-sm text-gray-700">Featured</label>
                </div>
              </div>
            </div>
            {/* Only show date inputs if addFeatured is true */}
            { addFeatured && (
              <div className="grid grid-cols-2 gap-4 sm:ml-4 mt-4 sm:mt-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Featured Start Date</label>
                  <input
                    type="datetime-local"
                    name="featuredStart"
                    className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Featured End Date</label>
                  <input
                    type="datetime-local"
                    name="featuredEnd"
                    className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <button
                type="submit"
                className="flex-1 bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 transition-colors text-sm font-medium"
              >
                Add Product
              </button>
              <button
                type="button"
                onClick={() => setShowAddProduct(false)}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Inline Edit Product Component */}
      {showEditProduct && editingProduct && (
        <div className="my-4 p-8 bg-white rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Edit Product</h3>
            <button
              onClick={() => { setShowEditProduct(false); setEditingProduct(null); }}
              className="p-2 hover:bg-gray-100/80 rounded-full transition-colors"
            >
              <XCircle className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <form className="space-y-6" onSubmit={handleUpdateProduct}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Name
              </label>
              <input
                name="productName"
                type="text"
                defaultValue={editingProduct.productName}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="productDescription"
                rows={3}
                defaultValue={editingProduct.productDescription}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Points
                </label>
                <input
                  name="pointCost"
                  type="number"
                  defaultValue={editingProduct.pointCost}
                  className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sale Points
                </label>
                <input
                  name="salePointCost"
                  type="number"
                  defaultValue={editingProduct.salePointCost}
                  className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                />
              </div>
            </div>
            <div>
              <p className="block text-sm font-medium text-gray-700 mb-2">
                Stock Type
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="edit-limited-stock"
                    name="stockType"
                    value="limited"
                    checked={editStockType === 'limited'}
                    onChange={() => setEditStockType('limited')}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="edit-limited-stock" className="text-sm text-gray-700">
                    Limited Stock
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="edit-unlimited-stock"
                    name="stockType"
                    value="unlimited"
                    checked={editStockType === 'unlimited'}
                    onChange={() => setEditStockType('unlimited')}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="edit-unlimited-stock" className="text-sm text-gray-700">
                    Unlimited Stock
                  </label>
                </div>
              </div>
            </div>
            
            {/* Only show stock quantity and max per student fields if stock type is limited */}
            {editStockType === 'limited' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock
                  </label>
                  <input
                    name="stockQuantity"
                    type="number"
                    defaultValue={editingProduct.stockQuantity}
                    className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Per Student
                  </label>
                  <input
                    name="maxPerStudent"
                    type="number"
                    defaultValue={editingProduct.maxPerStudent}
                    className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
              <MediaChooser
                label="Product Image"
                value={editImageUrl}
                onChange={(v) => setEditImageUrl(v)}
                allowManualUrlEntry={true}
                buttonText="Choose Image"
                clearable={true}
                imagesOnly={true}
              />
              <input type="hidden" name="imageUrl" value={editImageUrl} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pickup Location
              </label>
              <input
                name="pickupLocation"
                type="text"
                defaultValue={editingProduct.pickupLocation}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                placeholder="e.g., Room 101, Main Office"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Required Badge Level
              </label>
              <select
                name="requiredBadgeLevelId"
                value={editingProduct.requiredBadgeLevelId ?? ''}
                onChange={handleEditBadgeLevelSelect}
                className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              >
                <option value="">None</option>
                {badgeOptions.map(opt => (
                  <option key={opt.badgeLevelId} value={opt.badgeLevelId}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <p className="block text-sm font-medium text-gray-700 mb-2">Featured Status</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="edit-not-featured"
                    name="featuredStatus"
                    value="notFeatured"
                    checked={!editFeatured}
                    onChange={() => setEditFeatured(false)}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="edit-not-featured" className="text-sm text-gray-700">Not Featured</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="edit-featured"
                    name="featuredStatus"
                    value="featured"
                    checked={editFeatured}
                    onChange={() => setEditFeatured(true)}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="edit-featured" className="text-sm text-gray-700">Featured</label>
                </div>
              </div>
            </div>
            {/* Only show date inputs if editFeatured is true */}
            { editFeatured && (
              <div className="grid grid-cols-2 gap-4 sm:ml-4 mt-4 sm:mt-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Featured Start Date</label>
                  <input
                    type="datetime-local"
                    name="featuredStart"
                    defaultValue={editingProduct.featuredStart ? convertUTCtoLocal(editingProduct.featuredStart) : ''}
                    className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Featured End Date</label>
                  <input
                    type="datetime-local"
                    name="featuredEnd"
                    defaultValue={editingProduct.featuredEnd ? convertUTCtoLocal(editingProduct.featuredEnd) : ''}
                    className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                type="submit"
                className="flex-1 bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 transition-colors text-sm font-medium"
              >
                Update Product
              </button>
              <button
                type="button"
                onClick={() => { setShowEditProduct(false); setEditingProduct(null); }}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}